import React, { useEffect, useState, useMemo, useCallback, useRef } from "react"
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Col,
  Form,
  FormGroup,
  FormFeedback,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import Select from "react-select"
import FormLabel from "components/Extra/FormLabel"
import { FormInput } from "components/Extra/FormInput"
import { useTranslation } from "react-i18next"
import classNames from "classnames"
import { Coupon } from "../Coupons"
import { PreferredTimesModal } from "./PreferredTimesModal"

interface PendingApprovalModalProps {
  toggle: () => void
  isOpen: boolean
  coupon: Coupon | null
  onSubmit?: (data: any) => void
}

interface ConsumptionAddress {
  id: string
  label: string
  address: string
  coordinates: { lat: number; lng: number }
}

const dummyAddresses: ConsumptionAddress[] = [
  {
    id: "home",
    label: "Home",
    address: "Portsaid.23july",
    coordinates: { lat: 25.286106, lng: 51.534817 }, // Doha coordinates
  },
  {
    id: "work",
    label: "Work",
    address: "Portsaid.23july",
    coordinates: { lat: 25.354826, lng: 51.183884 }, // Another Doha location
  },
]

const daysOfWeek = [
  { value: "sat", label: "coupons_saturday", shortLabel: "Sat" },
  { value: "sun", label: "coupons_sunday", shortLabel: "Sun" },
  { value: "mon", label: "coupons_monday", shortLabel: "Mon" },
  { value: "tue", label: "coupons_tuesday", shortLabel: "Tue" },
  { value: "wed", label: "coupons_wednesday", shortLabel: "Wed" },
  { value: "thu", label: "coupons_thursday", shortLabel: "Thu" },
  { value: "fri", label: "coupons_friday", shortLabel: "Fri" },
]

const refillOptions = [
  { value: "1", label: "1 Day" },
  { value: "2", label: "2 Days" },
  { value: "3", label: "3 Days" },
  { value: "4", label: "4 Days" },
  { value: "5", label: "5 Days" },
  { value: "6", label: "6 Days" },
  { value: "7", label: "7 Days" },
]

export const PendingApprovalModal: React.FC<PendingApprovalModalProps> = ({
  isOpen,
  toggle,
  coupon,
  onSubmit,
}) => {
  const { t } = useTranslation()

  const [openFirstAccordion, setOpenFirstAccordion] = useState("0")
  const [openSecondAccordion, setOpenSecondAccordion] = useState("0")
  const [openThirdAccordion, setOpenThirdAccordion] = useState("0")
  const [activeAddressTab, setActiveAddressTab] = useState("home")
  const [selectedDays, setSelectedDays] = useState<Record<string, { enabled: boolean; time: string }>>({
    sat: { enabled: false, time: "notSelected" },
    sun: { enabled: false, time: "notSelected" },
    mon: { enabled: false, time: "notSelected" },
    tue: { enabled: false, time: "notSelected" },
    wed: { enabled: false, time: "notSelected" },
    thu: { enabled: false, time: "notSelected" },
    fri: { enabled: false, time: "notSelected" },
  })

    const [showPreferredTimesModal, setShowPreferredTimesModal] = useState(false)
  const [currentDayForTimeChange, setCurrentDayForTimeChange] = useState<string | null>(null)

  const [selectedRefillTimes, setSelectedRefillTimes] = useState<{
    value: string
    label: string
  } | null>({ value: "3", label: "3 Days" })
  const [daysValidationError, setDaysValidationError] = useState<string>("")
  const previousOpenRef = useRef(false)

  // Reset state only when modal transitions from closed to open
  useEffect(() => {
    if (isOpen && !previousOpenRef.current) {
      setSelectedDays({
        sat: { enabled: false, time: "notSelected" },
        sun: { enabled: false, time: "notSelected" },
        mon: { enabled: false, time: "notSelected" },
        tue: { enabled: false, time: "notSelected" },
        wed: { enabled: false, time: "notSelected" },
        thu: { enabled: false, time: "notSelected" },
        fri: { enabled: false, time: "notSelected" },
      })
      setSelectedRefillTimes({ value: "3", label: "3 Days" })
      setDaysValidationError("")
    }
    previousOpenRef.current = isOpen
  }, [isOpen])

  const validationSchema = useMemo(() => Yup.object({
    refillTimesPerWeek: Yup.number()
      .min(1)
      .max(7)
      .required(t("coupons_refillTimesPerWeekValidationMessage") as string),
    itemCountPerOnce: Yup.number().required(
      t("coupons_itemCountPerOnceValidationMessage") as string
    ),
  }), [t])

  const validation = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    validateOnMount: false,
    initialValues: {
      couponsSerial: coupon?.id || "",
      couponProduct: coupon?.count?.toString() || "",
      productSize: coupon?.productSize || "",
      price: coupon?.price ? parseInt(coupon.price) : 0,
      refillTimesPerWeek: 3,
      itemCountPerOnce: parseInt(coupon?.itemPerRefill || "3"),
    },
    validationSchema,
    onSubmit: values => {
      console.log(values)
      console.log("Selected days:", selectedDays)
      console.log("Active address:", activeAddressTab)

      // Prepare data for parent component
      const submitData = {
        ...values,
        selectedDays,
        activeAddressTab,
      }

      // Call parent onSubmit handler if provided
      if (onSubmit) {
        onSubmit(submitData)
      }

      validation.resetForm()
      toggle()
    },
  })

  // Validation for days selection
  const validateDaysSelection = useMemo(() => {
    const enabledDaysCount = Object.values(selectedDays).filter(d => d.enabled).length
    const refillCount = parseInt(selectedRefillTimes?.value || "0")

    // Check if number of enabled days matches refill times
    if (enabledDaysCount !== refillCount) {
      return t("coupons_daysCountMustMatchRefillTimes") as string
    }

    // Check if each enabled day has a time selected
    const hasUnselectedTimes = Object.values(selectedDays).some(
      d => d.enabled && (d.time === "notSelected" || !d.time)
    )
    if (hasUnselectedTimes) {
      return t("coupons_allSelectedDaysMustHaveTime") as string
    }

    return ""
  }, [selectedDays, selectedRefillTimes, t])

  useEffect(() => {
    setDaysValidationError(validateDaysSelection)
  }, [validateDaysSelection])

  const isSubmitDisabled = useMemo(() => {
    // Check if there are any validation errors
    const hasDaysError = !!daysValidationError

    // Check form validation - but don't rely on validation.isValid
    const hasFormErrors = Object.keys(validation.errors).length > 0

    // Explicit validation for requirements
    const enabledDaysCount = Object.values(selectedDays).filter(d => d.enabled).length
    const refillCount = parseInt(selectedRefillTimes?.value || "0")
    const hasUnselectedTimes = Object.values(selectedDays).some(
      d => d.enabled && (d.time === "notSelected" || !d.time)
    )

    return hasDaysError || hasFormErrors || enabledDaysCount !== refillCount || hasUnselectedTimes
  }, [daysValidationError, validation.errors, selectedDays, selectedRefillTimes])

  const openGoogleMaps = (address: ConsumptionAddress) => {
    const { lat, lng } = address.coordinates
    const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
    window.open(url, "_blank")
  }

  const toggleDay = useCallback((day: string) => {
    setSelectedDays(prev => {
      const currentEnabled = prev[day]?.enabled || false
      const newState = {
        ...prev,
        [day]: {
          enabled: !currentEnabled,
          time: !currentEnabled ? "notSelected" : (prev[day]?.time || "notSelected"),
        },
      }
      return newState
    })
  }, [])

  const openTimeSelector = (day: string) => {
    const dayData = selectedDays[day]
    if (dayData && dayData.enabled) {
      setCurrentDayForTimeChange(day)
      setShowPreferredTimesModal(true)
    }
  }

  const handleTimeChange = (time: string) => {
    if (currentDayForTimeChange) {
      setSelectedDays(prev => ({
        ...prev,
        [currentDayForTimeChange]: {
          ...prev[currentDayForTimeChange],
          time: time,
        },
      }))
      setCurrentDayForTimeChange(null)
    }
  }

  const handleCloseTimeModal = () => {
    setShowPreferredTimesModal(false)
    setCurrentDayForTimeChange(null)
  }

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle} tag="h3">
          <h4 className="fw-semibold font-size-24">
            {t("coupons_pendingApproval")}
          </h4>
        </ModalHeader>
        <ModalBody>
          <Form
            onSubmit={e => {
              e.preventDefault()
              validation.handleSubmit()
              return false
            }}
          >
            {/* Basic Info Section */}
            <Accordion open={openFirstAccordion}>
              <AccordionItem>
                <AccordionHeader
                  targetId="0"
                  onClick={() =>
                    setOpenFirstAccordion(openFirstAccordion === "0" ? "1" : "0")
                  }
                >
                  {t("common_basicInfo")}
                </AccordionHeader>
                <AccordionBody accordionId="0">
                  <FormInput
                    label={t("coupons_couponSerial") as string}
                    name="couponsSerial"
                    type="text"
                    validation={validation}
                    disabled
                  />
                  <FormInput
                    label={t("coupons_couponProduct") as string}
                    name="couponProduct"
                    type="text"
                    validation={validation}
                    disabled
                  />
                  <FormInput
                    label={t("coupons_productSize") as string}
                    name="productSize"
                    type="text"
                    validation={validation}
                    info={t("coupons_couponSizeInfo") as string}
                    disabled
                  />
                  <FormInput
                    label={t("common_priceCurrency") as string}
                    name="price"
                    type="number"
                    validation={validation}
                    info={t("coupons_priceInfo") as string}
                    disabled
                  />
                </AccordionBody>
              </AccordionItem>
            </Accordion>

            {/* Consumption Addresses Section */}
            <Accordion open={openSecondAccordion} className="mt-4">
              <AccordionItem>
                <AccordionHeader
                  targetId="0"
                  onClick={() =>
                    setOpenSecondAccordion(
                      openSecondAccordion === "0" ? "1" : "0"
                    )
                  }
                >
                  {t("coupons_consumptionAddresses")}
                </AccordionHeader>
                <AccordionBody accordionId="0">
                  {dummyAddresses.map(address => (
                    <div
                      key={address.id}
                      className={classNames(
                        "d-flex align-items-center justify-content-between mb-2 rounded",
                        {
                          "border": activeAddressTab !== address.id,
                          "bg-light": activeAddressTab === address.id,
                        }
                      )}
                      style={{ 
                        cursor: "pointer",
                        height: "48px",
                        padding: "0.375rem 0.75rem",
                      }}
                      onClick={() => {
                        setActiveAddressTab(address.id)
                        openGoogleMaps(address)
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <i className="mdi mdi-map-marker font-size-18"></i>
                        <div>
                          <div className="fw-semibold" style={{ fontSize: "14px" }}>{address.label}</div>
                          <div className="text-muted" style={{ fontSize: "11px" }}>{address.address}</div>
                        </div>
                      </div>
                      {activeAddressTab === address.id && (
                        <i className="mdi mdi-star text-primary font-size-18"></i>
                      )}
                    </div>
                  ))}
                </AccordionBody>
              </AccordionItem>
            </Accordion>

            {/* Water Refill Timing Section */}
            <Accordion open={openThirdAccordion} className="mt-4">
              <AccordionItem>
                <AccordionHeader
                  targetId="0"
                  onClick={() =>
                    setOpenThirdAccordion(openThirdAccordion === "0" ? "1" : "0")
                  }
                >
                  {t("coupons_waterRefillTimeing")}
                </AccordionHeader>
                <AccordionBody accordionId="0">
                  <FormGroup>
                    <FormLabel htmlFor="refillTimesPerWeek">
                      {t("coupons_refillTimesPerWeek")}
                    </FormLabel>
                    <Select
                      id="refillTimesPerWeek"
                      value={selectedRefillTimes}
                      onChange={(option: any) => {
                        setSelectedRefillTimes(option)
                        validation.setFieldValue("refillTimesPerWeek", parseInt(option?.value || "3"))
                      }}
                      options={refillOptions}
                      className="select2-selection"
                    />
                    <div className="d-flex align-items-center gap-1 ms-2 mt-1">
                      <i className="bx bx-info-circle"></i>
                      <small className="text-secondary">
                        {t("coupons_refillTimesPerWeekInfo")}
                      </small>
                    </div>
                  </FormGroup>

                  <FormInput
                    label={t("coupons_itemCountPerOnce") as string}
                    name="itemCountPerOnce"
                    type="text"
                    validation={validation}
                  />

                  <FormGroup className="mt-4">
                    <FormLabel>{t("coupons_preferredDaysForRefilling")}</FormLabel>
                    <Row className="g-3">
                      {daysOfWeek.map(day => {
                        const dayData = selectedDays[day.value]
                        if (!dayData) return null

                        return (
                          <Col xs="6" sm="4" md="3" key={`${day.value}-${dayData.enabled}`}>
                            <div className="border rounded p-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span
                                  className="fw-semibold"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => toggleDay(day.value)}
                                >
                                  {day.shortLabel}
                                </span>
                                <div className="form-check form-switch m-0">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`switch-${day.value}`}
                                    checked={!!dayData.enabled}
                                    onClick={() => toggleDay(day.value)}
                                  />
                                </div>
                              </div>
                              <div
                                className="d-flex align-items-center gap-1"
                                style={{
                                  cursor: dayData.enabled ? "pointer" : "default",
                                  opacity: dayData.enabled ? 1 : 0.5,
                                  fontSize: "13px",
                                }}
                                onClick={() => openTimeSelector(day.value)}
                              >
                                <i
                                  className="mdi mdi-clock-outline"
                                  style={
                                    dayData.enabled?
                                    {
                                    background: "linear-gradient(94.68deg, #095BA8 4.7%, #00A0DC 94.6%)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text"
                                  }:{}}
                                ></i>
                                <span>
                                  {dayData.enabled && dayData.time !== "notSelected"
                                    ? t(`coupons_${dayData.time}`)
                                    : t("coupons_notSelected")}
                                </span>
                              </div>
                            </div>
                          </Col>
                        )
                      })}
                    </Row>
                    {daysValidationError && (
                      <FormFeedback className="d-block">
                        {daysValidationError}
                      </FormFeedback>
                    )}
                  </FormGroup>
                </AccordionBody>
              </AccordionItem>
            </Accordion>

            <Row>
              <Col>
                <div className="d-flex justify-content-end gap-2 mt-4">
                  <button
                    type="submit"
                    className="btn bg-gradiant-blue text-white border-0"
                    disabled={isSubmitDisabled}
                  >
                    {t("common_submit")}
                  </button>
                  <button
                    onClick={toggle}
                    type="button"
                    className="btn btn-cancel text-cyan border border-cyan"
                  >
                    {t("common_cancel")}
                  </button>
                </div>
              </Col>
            </Row>
          </Form>
        </ModalBody>
      </Modal>

      {/* Preferred Times Modal */}
      <PreferredTimesModal
        isOpen={showPreferredTimesModal}
        toggle={handleCloseTimeModal}
        onConfirm={handleTimeChange}
        currentTime={currentDayForTimeChange ? selectedDays[currentDayForTimeChange].time : "earlyMorning"}
      />
    </>
  )
}
